import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, X, ChevronRight, Crown, Medal, Award, Users, Play, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

/**
 * Cinematic Podium Reveal - full-screen, dramatic, manual reveal.
 *
 * Props:
 * - api: API base URL
 * - token: auth token
 * - competitionId: id
 * - onClose: callback
 * - initialMode: "both" | "leaders" | "followers" (organizer-selectable)
 * - initialRound: optional integer
 */
export default function CinematicPodiumReveal({ api, token, competitionId, onClose, initialMode = "both", initialRound }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [phase, setPhase] = useState("intro");
  // phase progression for "both": intro -> leaders-3 -> leaders-2 -> leaders-1 -> celebrate-leaders ->
  // followers-3 -> followers-2 -> followers-1 -> celebrate-followers -> done
  // For single-role mode, leaders/followers replaced by that role only.
  const [showConfetti, setShowConfetti] = useState(false);

  const headers = useMemo(
    () => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    const url = `${api}/competitions/${competitionId}/podium${initialRound ? `?round_number=${initialRound}` : ""}`;
    axios
      .get(url, headers)
      .then((r) => {
        if (active) setData(r.data);
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.detail || "Failed to load podium data");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, competitionId, initialRound, headers]);

  const phases = useMemo(() => {
    if (mode === "leaders") {
      return ["intro", "leaders-3", "leaders-2", "leaders-1", "celebrate-leaders", "done"];
    }
    if (mode === "followers") {
      return ["intro", "followers-3", "followers-2", "followers-1", "celebrate-followers", "done"];
    }
    return [
      "intro",
      "followers-3",
      "followers-2",
      "followers-1",
      "celebrate-followers",
      "leaders-3",
      "leaders-2",
      "leaders-1",
      "celebrate-leaders",
      "done",
    ];
  }, [mode]);

  const phaseIndex = phases.indexOf(phase);

  const advance = useCallback(() => {
    setPhase((cur) => {
      const i = phases.indexOf(cur);
      const next = phases[Math.min(i + 1, phases.length - 1)];
      if (next === "celebrate-leaders" || next === "celebrate-followers") {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 6500);
      }
      return next;
    });
  }, [phases]);

  const reset = useCallback(() => {
    setPhase("intro");
    setShowConfetti(false);
  }, []);

  // Keyboard: space / right arrow to advance, Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === " " || e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        advance();
      }
      if (e.key === "r" || e.key === "R") reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance, reset, onClose]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" data-testid="cinematic-loading">
        <div className="text-[#FFD700] font-['Playfair_Display'] text-2xl animate-pulse">Preparing the stage…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" data-testid="cinematic-error">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || "No data"}</p>
          <Button onClick={onClose} className="btn-outline">Close</Button>
        </div>
      </div>
    );
  }

  const leaders = data.leaders || [];
  const followers = data.followers || [];

  // Determine current visible state (which slots show which dancer)
  const visibleSet = (role) => {
    const arr = role === "leaders" ? leaders : followers;
    const isCelebrate = phase === `celebrate-${role}` || phase === "done";
    const reached3 = phaseIndex >= phases.indexOf(`${role}-3`) || isCelebrate;
    const reached2 = phaseIndex >= phases.indexOf(`${role}-2`) || isCelebrate;
    const reached1 = phaseIndex >= phases.indexOf(`${role}-1`) || isCelebrate;
    return {
      third: reached3 ? arr[2] : null,
      second: reached2 ? arr[1] : null,
      first: reached1 ? arr[0] : null,
      isCelebrate,
    };
  };

  const currentRole =
    phase.startsWith("leaders") || phase === "celebrate-leaders"
      ? "leaders"
      : phase.startsWith("followers") || phase === "celebrate-followers"
      ? "followers"
      : mode === "leaders"
      ? "leaders"
      : mode === "followers"
      ? "followers"
      : "followers"; // default starting role for both

  const roleColor = currentRole === "leaders" ? "#FFD700" : "#FF007F";
  const roleLabel = currentRole === "leaders" ? "LEADERS" : "FOLLOWERS";

  const vis = visibleSet(currentRole);

  const lastPhase = phase === "done";
  const showPodium = phase !== "intro";

  return (
    <div
      className="fixed inset-0 z-[100] overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at top, #1a0a2e 0%, #0a0a0f 60%, #000 100%)",
      }}
      data-testid="cinematic-podium-reveal"
    >
      {/* Backdrop spotlights */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-30 blur-3xl"
          style={{ background: `radial-gradient(circle, ${roleColor}40 0%, transparent 70%)` }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,215,0,0.15),transparent_50%)]" />
        {/* Subtle stars */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(white 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }} />
      </div>

      {/* HERO LOGOS — wrapped in a flex row so both share the EXACT same vertical axis.
          Left = event logo (2/3 size). Right = JackAndJill brand mark (larger). */}
      <div className="pointer-events-none absolute inset-y-0 inset-x-4 lg:inset-x-10 z-[5] flex items-center justify-between">
        {data.festival_logo_base64 ? (
          <motion.img
            src={data.festival_logo_base64}
            alt="event-logo"
            initial={{ opacity: 0, scale: 0.7, x: -40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden md:block h-28 lg:h-40 w-auto object-contain"
            style={{ filter: `drop-shadow(0 0 30px ${roleColor}45)` }}
            data-testid="cinematic-event-logo-big"
          />
        ) : (
          <div className="hidden md:block" />
        )}
        <motion.img
          src="/jj-logo.png"
          alt="jackandjill-logo"
          initial={{ opacity: 0, scale: 0.7, x: 40 }}
          animate={{ opacity: 0.95, scale: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="hidden md:block h-80 lg:h-[28rem] w-auto object-contain"
          style={{ filter: "drop-shadow(0 0 60px rgba(255,215,0,0.55))" }}
          data-testid="cinematic-jj-logo"
        />
      </div>

      {/* Confetti + Fireworks */}
      <AnimatePresence>
        {showConfetti && (
          <>
            <ConfettiBurst color={roleColor} />
            <FireworksBurst color={roleColor} />
          </>
        )}
      </AnimatePresence>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {data.festival_logo_base64 && (
            <img src={data.festival_logo_base64} alt="logo" className="h-10 rounded" />
          )}
          <div>
            <p className="text-white font-['Playfair_Display'] font-bold text-lg leading-tight">
              {data.festival_name}
            </p>
            <p className="text-[#A1A1AA] text-xs uppercase tracking-widest">
              {data.category} · {data.round_name || `Round ${data.round_number}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode toggle - only available at intro */}
          {phase === "intro" && (
            <div className="flex bg-white/5 rounded-full p-1" data-testid="cinematic-mode-toggle">
              {[
                { v: "both", label: "Both (F→L)" },
                { v: "leaders", label: "Leaders only" },
                { v: "followers", label: "Followers only" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setMode(opt.v)}
                  className={`px-3 py-1.5 text-xs rounded-full transition ${
                    mode === opt.v ? "bg-[#FFD700] text-black font-semibold" : "text-white/70 hover:text-white"
                  }`}
                  data-testid={`cinematic-mode-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <Button onClick={reset} variant="outline" size="sm" className="btn-outline" data-testid="cinematic-reset-btn">
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button onClick={onClose} variant="outline" size="sm" className="btn-outline" data-testid="cinematic-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6" style={{ minHeight: "calc(100vh - 160px)" }}>
        {phase === "intro" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl"
          >
            <Trophy className="w-24 h-24 mx-auto mb-8 text-[#FFD700]" />
            <h1 className="font-['Playfair_Display'] text-6xl md:text-7xl font-bold text-white mb-4">
              And the winners are…
            </h1>
            <p className="text-[#A1A1AA] text-lg mb-10">
              Press <kbd className="px-2 py-0.5 bg-white/10 rounded text-white">SPACE</kbd> or click below to reveal the podium step by step.
            </p>
            <Button
              size="lg"
              onClick={advance}
              className="btn-gold text-lg px-10 py-6"
              data-testid="cinematic-start-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Begin reveal
            </Button>
          </motion.div>
        )}

        {showPodium && (
          <div className="w-full max-w-6xl">
            <motion.div
              key={currentRole}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <p
                className="font-['Playfair_Display'] uppercase tracking-[0.4em] text-sm mb-1"
                style={{ color: roleColor }}
              >
                Top 3
              </p>
              <h2 className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold text-white">
                {roleLabel}
              </h2>
            </motion.div>

            <div className="flex items-end justify-center gap-4 md:gap-8">
              <PodiumSlot
                position={3}
                color="#CD7F32"
                dancer={vis.third}
                roleColor={roleColor}
              />
              <PodiumSlot
                position={1}
                color="#FFD700"
                dancer={vis.first}
                roleColor={roleColor}
                isWinner={vis.isCelebrate}
              />
              <PodiumSlot
                position={2}
                color="#C0C0C0"
                dancer={vis.second}
                roleColor={roleColor}
              />
            </div>
          </div>
        )}

        {lastPhase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-12 text-center"
          >
            <h3 className="font-['Playfair_Display'] text-3xl text-white mb-4">
              Congratulations to all winners!
            </h3>
            <Button onClick={reset} className="btn-outline mr-2" data-testid="cinematic-replay-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Replay
            </Button>
            <Button onClick={onClose} className="btn-gold" data-testid="cinematic-done-btn">
              Close
            </Button>
          </motion.div>
        )}
      </div>

      {/* Bottom click-to-advance hint */}
      {phase !== "intro" && !lastPhase && (
        <button
          onClick={advance}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 text-white/70 hover:text-white px-5 py-3 rounded-full bg-white/5 backdrop-blur transition group"
          data-testid="cinematic-next-btn"
        >
          <span className="font-medium tracking-wide">
            {phase === `celebrate-${currentRole}`
              ? mode === "both" && currentRole === "followers"
                ? "Continue to Leaders"
                : "Finish"
              : "Reveal next"}
          </span>
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
        </button>
      )}
    </div>
  );
}

function PodiumSlot({ position, color, dancer, roleColor, isWinner }) {
  const heightClass =
    position === 1 ? "h-72 md:h-80" : position === 2 ? "h-56 md:h-64" : "h-44 md:h-52";
  const icon =
    position === 1 ? (
      <Crown className="w-8 h-8" style={{ color }} />
    ) : position === 2 ? (
      <Medal className="w-7 h-7" style={{ color }} />
    ) : (
      <Award className="w-6 h-6" style={{ color }} />
    );

  return (
    <div className="flex flex-col items-center" style={{ width: position === 1 ? "min(280px, 32vw)" : "min(220px, 28vw)" }}>
      {/* Dancer card */}
      <div className="h-32 md:h-40 mb-4 flex flex-col items-center justify-end">
        <AnimatePresence mode="wait">
          {dancer ? (
            <motion.div
              key={dancer.dancer_id}
              initial={{ opacity: 0, y: -60, scale: 0.4 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: isWinner && position === 1 ? 1.08 : 1,
              }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="text-center"
            >
              <div
                className="inline-flex flex-col items-center px-5 py-3 rounded-xl backdrop-blur"
                style={{
                  background: `linear-gradient(180deg, ${color}30, transparent)`,
                  border: `1px solid ${color}80`,
                  boxShadow: `0 0 40px ${color}40`,
                }}
              >
                {icon}
                <p className="text-white font-['Playfair_Display'] font-bold text-xl md:text-2xl mt-1 leading-tight">
                  {dancer.name}
                </p>
                <p className="text-xs text-white/70 uppercase tracking-widest">
                  #{dancer.bib_number} · {dancer.country || "—"}
                </p>
                {dancer.combined_score !== undefined && dancer.combined_score > 0 && (
                  <p className="text-base font-bold mt-1" style={{ color: roleColor }}>
                    {Number(dancer.combined_score).toFixed(2)}
                  </p>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              className="text-white/30 text-xs uppercase tracking-widest"
            >
              <Users className="w-6 h-6 mx-auto opacity-50" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Podium block */}
      <motion.div
        initial={{ scaleY: 0, originY: 1 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 * position }}
        className={`${heightClass} w-full rounded-t-lg relative overflow-hidden`}
        style={{
          background: `linear-gradient(180deg, ${color}cc, ${color}66)`,
          boxShadow: `0 -10px 40px -10px ${color}80`,
        }}
      >
        <div className="absolute inset-x-0 top-3 text-center">
          <span className="font-['Playfair_Display'] font-bold text-5xl md:text-6xl text-black/60">
            {position}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

function ConfettiBurst({ color }) {
  const pieces = Array.from({ length: 180 });
  // Vibrant palette: gold, magenta, cyan, lime, orange, white, role color
  const palette = [color, "#FFD700", "#FF007F", "#00E5FF", "#A3E635", "#FF8A00", "#ffffff", "#9333EA", "#22D3EE"];
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" data-testid="cinematic-confetti">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.2;
        const duration = 2.4 + Math.random() * 2.6;
        const size = 6 + Math.random() * 14;
        const rotate = Math.random() * 360;
        const drift = (Math.random() - 0.5) * 200;
        const shape = i % 3; // 0=square, 1=circle, 2=ribbon
        const bg = palette[i % palette.length];
        return (
          <motion.div
            key={`confetti-${i}`}
            initial={{ y: -40, x: 0, opacity: 0, rotate }}
            animate={{ y: "110vh", x: drift, opacity: 1, rotate: rotate + 720 }}
            transition={{ duration, delay, ease: "easeIn" }}
            className="absolute"
            style={{
              left: `${left}%`,
              width: size,
              height: shape === 2 ? size * 0.3 : size * 0.7,
              background: bg,
              borderRadius: shape === 1 ? "50%" : 2,
              boxShadow: `0 0 10px ${bg}80`,
            }}
          />
        );
      })}
    </div>
  );
}

function FireworksBurst({ color }) {
  // 6 simultaneous bursts at random positions, each with 24 sparks
  const bursts = Array.from({ length: 6 });
  const palette = [color, "#FFD700", "#FF007F", "#00E5FF", "#A3E635", "#FF8A00", "#9333EA"];
  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden" data-testid="cinematic-fireworks">
      {bursts.map((_, b) => {
        const cx = 10 + Math.random() * 80; // % from left
        const cy = 15 + Math.random() * 50; // % from top
        const burstColor = palette[b % palette.length];
        const burstDelay = b * 0.45 + Math.random() * 0.3;
        const sparks = Array.from({ length: 28 });
        return (
          <div
            key={`burst-${b}`}
            className="absolute"
            style={{ left: `${cx}%`, top: `${cy}%`, width: 0, height: 0 }}
          >
            {/* Bright flash */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 2.5, 0], opacity: [0, 1, 0] }}
              transition={{ duration: 0.8, delay: burstDelay, ease: "easeOut" }}
              className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                width: 60,
                height: 60,
                background: `radial-gradient(circle, ${burstColor} 0%, transparent 70%)`,
                filter: "blur(4px)",
              }}
            />
            {/* Sparks */}
            {sparks.map((_, s) => {
              const angle = (s / sparks.length) * Math.PI * 2;
              const distance = 120 + Math.random() * 100;
              const dx = Math.cos(angle) * distance;
              const dy = Math.sin(angle) * distance;
              const sparkColor = palette[(b + s) % palette.length];
              return (
                <motion.div
                  key={`spark-${b}-${s}`}
                  initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                  animate={{ x: dx, y: dy, opacity: [0, 1, 1, 0], scale: [0, 1, 1, 0.5] }}
                  transition={{ duration: 1.6, delay: burstDelay, ease: "easeOut" }}
                  className="absolute"
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: sparkColor,
                    boxShadow: `0 0 12px ${sparkColor}, 0 0 24px ${sparkColor}80`,
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
